import { useCallback, useState } from "react";
import PDAHeader from "src/components/common/Header";
import SpinnerOverlay from "src/components/common/SpinnerOverlay";
import { usePDAContainer } from "src/hooks/usePDAContainer";
import { usePDA1050_R01Content } from "./PDA1050_R01Content";
import { PDA1050_R01HeaderDto } from "./PDA1050_R01Dto";
import PDA1050_R01Form from "./PDA1050_R01Form";
import PDA1050_R01OrderList from "./PDA1050_R01OrderList";
import PDA1050_R01Search from "./PDA1050_R01Search";
import PDA1050_R01Table from "./PDA1050_R01Table";
import usePDA1050_R01Store from "./Store_PDA1050_R01";

interface PDA1050_R01ContainerProps {
  menuNm: string;
  menuCd?: string;
  onBack?: () => void;
}

/**
 * PDA1050_R01의 목록 화면과 입고등록 상세 화면을 전환하는 최상위 컨테이너다.
 * 발주 목록에서는 검색/선택을 제공하고, 발주 선택 후에는 스캔 폼과 품목 현황 그리드를 표시한다.
 */
const PDA1050_R01Container = ({
  menuNm,
  menuCd,
  onBack,
}: PDA1050_R01ContainerProps) => {
  const {
    isLoading,
    PDA1050_R01List,
    initData,
    initScan,
    setCheckedPDA1050_R01List,
  } = usePDA1050_R01Store();
  const [showList, setShowList] = useState(true);

  /** 메뉴 전환 시 Store 전체를 초기화하고 첫 화면인 발주 목록으로 이동한다. */
  const resetScreen = useCallback(() => {
    initData();
    setShowList(true);
  }, [initData]);

  const { handleMenuClick } = usePDAContainer(resetScreen);
  const {
    handleSearchOrders,
    handleSelectOrder,
    handleScanBarcode,
    handleCheck,
    handleComplete,
  } = usePDA1050_R01Content();

  /**
   * 선택한 발주의 상세 조회가 성공한 경우에만 입고등록 화면으로 전환한다.
   *
   * @param order 목록 그리드에서 선택한 발주 Header
   */
  const handleOrderSelect = async (order: PDA1050_R01HeaderDto) => {
    const isSelected = await handleSelectOrder(order);
    if (isSelected) {
      setShowList(false);
    }
  };

  /**
   * 상세 화면에서 임시 검수 목록과 현재 스캔값을 제거하고 발주 목록으로 돌아간다.
   * 검색 조건과 조회된 발주 목록은 유지하여 사용자가 같은 위치에서 발주를 다시 선택할 수 있게 한다.
   */
  const handleBackToList = () => {
    setCheckedPDA1050_R01List([]);
    initScan();
    setShowList(true);
  };

  return (
    <div className="w-full h-screen flex flex-col bg-white fixed inset-0">
      <PDAHeader
        menuNm={menuNm}
        menuCd={menuCd}
        onBack={showList ? onBack : handleBackToList}
        onMenuClick={handleMenuClick}
      />
      <div className="flex-1 overflow-hidden">
        {showList ? (
          <div className="h-full flex flex-col overflow-hidden">
            <PDA1050_R01Search onSearch={handleSearchOrders} />
            <PDA1050_R01OrderList
              orders={PDA1050_R01List}
              onSelectOrder={handleOrderSelect}
            />
          </div>
        ) : (
          <div className="h-full flex flex-col overflow-hidden">
            <div className="h-2/4 border-b border-gray-200 overflow-y-auto">
              <PDA1050_R01Form
                onChangeOrder={handleBackToList}
                onScanBarcode={handleScanBarcode}
                onCheck={handleCheck}
                onComplete={handleComplete}
              />
            </div>
            <div className="h-2/4 overflow-y-auto">
              <PDA1050_R01Table />
            </div>
          </div>
        )}
      </div>
      {isLoading && <SpinnerOverlay text="처리 중입니다..." />}
    </div>
  );
};

export default PDA1050_R01Container;
